// Sales Report Hook
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import type { DateRange, SalesReport, SalesMetrics, SalesByProduct, SalesByClient } from '@/types/report';
import { calculateGrowthRate, calculateAverage } from '../utils/calculations';
import { getPreviousPeriod } from '../utils/periods';

export function useSalesReport(period: DateRange) {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['sales-report', company?.id, period.start.toISOString(), period.end.toISOString()],
    queryFn: async (): Promise<SalesReport> => {
      if (!company?.id) throw new Error('No company selected');

      // Fetch invoices for the period
      const { data: invoices, error } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          created_at,
          total_ht,
          total_tva,
          total_ttc,
          client_id,
          clients (
            id,
            name
          ),
          invoice_items (
            id,
            product_id,
            quantity,
            unit_price,
            total_ht,
            products (
              id,
              name,
              category
            )
          )
        `)
        .eq('company_id', company.id)
        .gte('created_at', period.start.toISOString())
        .lte('created_at', period.end.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch previous period data for comparison
      const previousPeriod = getPreviousPeriod(period);
      const { data: previousInvoices } = await supabase
        .from('invoices')
        .select('total_ttc')
        .eq('company_id', company.id)
        .gte('created_at', previousPeriod.start.toISOString())
        .lte('created_at', previousPeriod.end.toISOString());

      // Calculate metrics
      const totalRevenue = invoices?.reduce((sum, inv) => sum + (inv.total_ttc || 0), 0) || 0;
      const totalRevenueHT = invoices?.reduce((sum, inv) => sum + (inv.total_ht || 0), 0) || 0;
      const totalTVA = invoices?.reduce((sum, inv) => sum + (inv.total_tva || 0), 0) || 0;
      const invoiceCount = invoices?.length || 0;
      const averageInvoice = calculateAverage(totalRevenue, invoiceCount);

      const previousRevenue = previousInvoices?.reduce((sum, inv) => sum + (inv.total_ttc || 0), 0) || 0;
      const growthRate = calculateGrowthRate(totalRevenue, previousRevenue);

      const metrics: SalesMetrics = {
        totalRevenue,
        totalRevenueHT,
        totalTVA,
        invoiceCount,
        averageInvoice,
        growthRate,
      };

      // Group by product
      const productMap = new Map<string, SalesByProduct>();
      
      invoices?.forEach(invoice => {
        invoice.invoice_items?.forEach(item => {
          const product = item.products as any;
          if (!product) return;

          const existing = productMap.get(product.id);
          if (existing) {
            existing.quantity += item.quantity || 0;
            existing.revenue += (item.total_ht || 0) * 1.19; // Add TVA
            existing.revenueHT += item.total_ht || 0;
          } else {
            productMap.set(product.id, {
              productId: product.id,
              productName: product.name,
              quantity: item.quantity || 0,
              revenue: (item.total_ht || 0) * 1.19,
              revenueHT: item.total_ht || 0,
            });
          }
        });
      });

      const byProduct = Array.from(productMap.values())
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 20); // Top 20 products

      // Group by client
      const clientMap = new Map<string, SalesByClient>();
      
      invoices?.forEach(invoice => {
        const client = invoice.clients as any;
        if (!client) return;

        const existing = clientMap.get(client.id);
        if (existing) {
          existing.invoiceCount += 1;
          existing.revenue += invoice.total_ttc || 0;
          existing.revenueHT += invoice.total_ht || 0;
        } else {
          clientMap.set(client.id, {
            clientId: client.id,
            clientName: client.name,
            invoiceCount: 1,
            revenue: invoice.total_ttc || 0,
            revenueHT: invoice.total_ht || 0,
          });
        }
      });

      const byClient = Array.from(clientMap.values())
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 20); // Top 20 clients

      // Create trend data (daily aggregation)
      const trendMap = new Map<string, { revenue: number; revenueHT: number; count: number }>();
      
      invoices?.forEach(invoice => {
        const dateKey = new Date(invoice.created_at).toISOString().split('T')[0];
        const existing = trendMap.get(dateKey);
        
        if (existing) {
          existing.revenue += invoice.total_ttc || 0;
          existing.revenueHT += invoice.total_ht || 0;
          existing.count += 1;
        } else {
          trendMap.set(dateKey, {
            revenue: invoice.total_ttc || 0,
            revenueHT: invoice.total_ht || 0,
            count: 1,
          });
        }
      });

      const trend = Array.from(trendMap.entries())
        .map(([date, data]) => ({
          date,
          revenue: Math.round(data.revenue * 100) / 100,
          revenueHT: Math.round(data.revenueHT * 100) / 100,
          invoiceCount: data.count,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      return {
        period,
        metrics,
        byProduct,
        byClient,
        trend,
      };
    },
    enabled: !!company?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
